import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHome, faClipboardList, faUsers, faCog, faBars, faUser, faSignOutAlt,
  faCogs, faPaperPlane, faChartBar, faBell, faBoxOpen, faDownload, faGavel, faBox
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../auth/AuthContext';
import wedlLogo from '../../assets/images/wedl_logo.svg';
import './Navbar.css';

const Navbar: React.FC = () => {
  const { 
    user, 
    logout, 
    canAccessSubmissions, 
    canAccessQuestionnaires, 
    canAccessSuppliers,
    isAdmin,
    userEKGroups,
    getUserEKCodes,
    hasMenuPermission
  } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const navRef = useRef<HTMLDivElement>(null);
  const navbarToggleRef = useRef<HTMLButtonElement>(null);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsOpen(false);
  }, [location]);
  
  // Prevent scrolling when mobile menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);
  
  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: Event) {
      const target = event.target as Element | null;
      
      if (navRef.current && isOpen && 
          !navRef.current.contains(target as Node) && 
          !target?.classList?.contains('wedl-navbar-toggle') &&
          !target?.closest('.wedl-navbar-toggle')) {
        setIsOpen(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen]);

  const handleLogout = async () => {
    try {
      await logout();
      setIsOpen(false);
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };
  
  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };
  
  const handleMenuClick = (e: React.MouseEvent, path: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    setIsOpen(false);
    
    setTimeout(() => {
      navigate(path);
    }, 100);
  };

  // **BERECHTIGUNGSBASIERTE MENÜ-STRUKTUR**
  const allMenuItems = [
    { 
      path: "/dashboard", 
      label: "Dashboard", 
      icon: faHome,
      description: "Übersicht und Statistiken",
      requiresPermission: null // Immer sichtbar
    },
    { 
      path: "/questionnaires", 
      label: "Fragebögen", 
      icon: faClipboardList,
      description: "Templates verwalten",
      requiresPermission: "questionnaires"
    },
    { 
      path: "/submissions", 
      label: "Submissions", 
      icon: faPaperPlane,
      description: "Versendungen verwalten",
      requiresPermission: "submissions"
    },
    { 
      path: "/suppliers", 
      label: "Lieferanten", 
      icon: faUsers,
      description: "Lieferanten verwalten",
      requiresPermission: "suppliers"
    },
    { 
      path: "/articles", 
      label: "Artikel", 
      icon: faBoxOpen,
      description: "LMIV-Artikeldaten verwalten",
      requiresPermission: "articles"
    },
    { 
      path: "/master-articles", 
      label: "Master-Artikel", 
      icon: faBox,
      description: "Master-Artikel für Auktionen",
      requiresPermission: "articles"
    },
    { 
      path: "/auctions", 
      label: "Auktionen", 
      icon: faGavel,
      description: "Rückwärtsauktionen verwalten",
      requiresPermission: "articles"
    },
    { 
      path: "/export", 
      label: "Export", 
      icon: faDownload,
      description: "Perfion Daten-Export",
      requiresPermission: "export"
    },
    { 
      path: "/analytics", 
      label: "Analytics", 
      icon: faChartBar,
      description: "Berichte und Analysen",
      requiresPermission: "submissions" // Analytics benötigt Submission-Zugriff
    },
    { 
      path: "/workflows", 
      label: "Workflows", 
      icon: faCogs,
      description: "Automatisierung verwalten",
      requiresPermission: "admin" // Nur für Fragebogen_Administrator
    },
    { 
      path: "/settings", 
      label: "Settings", 
      icon: faCog,
      description: "Systemeinstellungen verwalten",
      requiresPermission: "admin" // Nur für supplierportal_admin
    },
  ];

  // **MENÜ-ITEMS BASIEREND AUF BERECHTIGUNGEN FILTERN**
  const menuItems = allMenuItems.filter(item => {
    if (!item.requiresPermission) return true; // Dashboard ist immer sichtbar
    
    switch (item.requiresPermission) {
      case 'questionnaires':
        return canAccessQuestionnaires;
      case 'submissions':
        return canAccessSubmissions;
      case 'suppliers':
        return canAccessSuppliers;
      case 'articles':
        return hasMenuPermission('articles');
      case 'export':
        return hasMenuPermission('export');
      case 'admin':
        return isAdmin;
      default:
        return false;
    }
  });

  // **DEBUG: Log der sichtbaren Menüs**
  console.log('[NAVBAR] Menu visibility:', {
    userEKGroups,
    ekCodes: getUserEKCodes(),
    canAccessSubmissions,
    canAccessQuestionnaires,
    canAccessSuppliers,
    hasArticlesAccess: hasMenuPermission('articles'),
    hasExportAccess: hasMenuPermission('export'),
    visibleMenus: menuItems.map(m => m.label)
  });

  return (
    <nav className="wedl-navbar bg-dark navbar-dark">
      <div className="navbar-container">
        {/* Logo und Branding */}
        <Link to="/dashboard" className="wedl-navbar-brand">
          <img 
            src={wedlLogo} 
            alt="WEDL Logo" 
            className="wedl-navbar-logo"
          />
          <span className="wedl-brand-text">Lieferantenportal</span>
        </Link>
        
        {/* Mobile Toggle Button */}
        <button 
          ref={navbarToggleRef}
          className={`wedl-navbar-toggle ${isOpen ? 'active' : ''}`}
          onClick={toggleMenu}
          aria-controls="basic-navbar-nav"
          aria-expanded={isOpen}
        >
          <div className="wedl-toggle-icon">
            <FontAwesomeIcon icon={faBars} />
          </div>
        </button>
        
        {/* Navigation Container */}
        <div 
          id="basic-navbar-nav" 
          ref={navRef}
          className={`wedl-navbar-collapse ${isOpen ? 'show' : ''}`}
        >
          {/* Mobile Nav Header - NUR AUF MOBILE */}
          <div className="wedl-mobile-nav-header d-lg-none">
            <div className="flex items-center justify-between p-4">
              <span className="text-white font-medium">Navigation</span>
              <button 
                onClick={() => setIsOpen(false)}
                className="text-white hover:text-gray-300"
              >
                <FontAwesomeIcon icon={faSignOutAlt} />
              </button>
            </div>
          </div>
          
          {/* Menüpunkte */}
          <div className="wedl-main-nav-container">
            <nav className="wedl-main-nav">
              {menuItems.map((item) => (
                <div key={item.path} className="wedl-nav-item">
                  <Link
                    to={item.path}
                    onClick={(e) => handleMenuClick(e, item.path)}
                    className={`wedl-nav-link ${location.pathname === item.path ? 'active' : ''}`}
                    title={item.description}
                  >
                    <FontAwesomeIcon icon={item.icon} className="wedl-nav-icon" />
                    <span className="nav-text">{item.label}</span>
                    {(item as any).isNew && (
                      <span className="ml-2 px-1.5 py-0.5 bg-red-500 text-white text-xs rounded-full animate-pulse">
                        NEU
                      </span>
                    )}
                  </Link>
                </div>
              ))}
            </nav>
          </div>
        </div>
          
        {/* User Section */}
        {user && (
          <div className="wedl-user-section">
            <div className="wedl-user-avatar">
              <FontAwesomeIcon icon={faUser} />
            </div>
            
            <span className="wedl-user-name">
              {user.name || user.username}
            </span>
            
            <button 
              onClick={handleLogout} 
              className="btn btn-outline-light wedl-logout-btn"
              title="Abmelden"
            >
              <FontAwesomeIcon icon={faSignOutAlt} className="me-1" />
              <span className="btn-text">Abmelden</span>
            </button>
          </div>
        )}
        
        {/* Overlay für bessere Interaktion */}
        {isOpen && <div className="wedl-navbar-overlay" onClick={() => setIsOpen(false)}></div>}
      </div>
    </nav>
  );
};

export default Navbar;